import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Tabs,
  Tab,
  Stack,
  Divider,
  Alert
} from '@mui/material'
import {
  FileDownload,
  Refresh,
  PictureAsPdf,
  TableChart
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'

import FinancialDashboard from './FinancialDashboard'
import FinancialTable from './FinancialTable'
import {
  getFinancialSummary,
  getCommissionDetails,
  getUsedCoupons,
  getWalletTransactions,
  getFinancialTimeSeriesData,
  FinancialSummary
} from '../../services/financialService'
import {
  exportToCSV,
  exportToExcel,
  exportMultipleSheetsToExcel,
  generateFinancialReportPDF
} from '../../services/exportService'

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8']

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  )
}

export default function ReportsPage() {
  const [loading, setLoading] = useState(false)
  const [financialData, setFinancialData] = useState<FinancialSummary | null>(null)
  const [commissions, setCommissions] = useState<any[]>([])
  const [coupons, setCoupons] = useState<any[]>([])
  const [walletTransactions, setWalletTransactions] = useState<any[]>([])
  const [timeSeriesData, setTimeSeriesData] = useState<any[]>([])
  
  const [startDate, setStartDate] = useState<Date>(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
  )
  const [endDate, setEndDate] = useState<Date>(new Date())
  const [period, setPeriod] = useState<string>('')
  const [currentTab, setCurrentTab] = useState(0)

  useEffect(() => {
    if (period !== '') {
      loadData()
    } else {
      // Clear data when period is not selected
      setFinancialData(null)
      setCommissions([])
      setCoupons([])
      setWalletTransactions([])
      setTimeSeriesData([])
      setLoading(false)
    }
  }, [startDate, endDate, period])

  const loadData = async () => {
    if (period === '') {
      return
    }
    
    try {
      setLoading(true)
      
      const [summary, commissionsData, couponsData, walletData, timeSeries] = await Promise.all([
        getFinancialSummary(startDate, endDate),
        getCommissionDetails(startDate, endDate),
        getUsedCoupons(startDate, endDate),
        getWalletTransactions(startDate, endDate),
        getFinancialTimeSeriesData(startDate, endDate, 'daily')
      ])
      
      setFinancialData(summary)
      setCommissions(commissionsData)
      setCoupons(couponsData)
      setWalletTransactions(walletData.transactions)
      setTimeSeriesData(timeSeries)
    } catch (error) {
      console.error('Error loading financial data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handlePeriodChange = (newPeriod: string) => {
    setPeriod(newPeriod)
    const now = new Date()
    
    switch (newPeriod) {
      case '':
        // Reset to default dates when "none" is selected
        setStartDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))
        setEndDate(new Date())
        break
      case 'today':
        setStartDate(new Date(now.setHours(0, 0, 0, 0)))
        setEndDate(new Date())
        break
      case '7days':
        setStartDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000))
        setEndDate(new Date())
        break
      case '30days':
        setStartDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))
        setEndDate(new Date())
        break
      case 'thisMonth':
        setStartDate(new Date(now.getFullYear(), now.getMonth(), 1))
        setEndDate(new Date())
        break
      case 'custom':
        break
    }
  }

  const handleExportPDF = () => {
    if (financialData) {
      generateFinancialReportPDF(
        financialData,
        `financial_report_${new Date().toISOString().split('T')[0]}`
      )
    }
  }

  const handleExportExcel = () => {
    const sheets = []
    
    if (commissions.length > 0) {
      sheets.push({
        name: 'العمولات',
        data: commissions.map(c => ({
          'اسم التاجر': c.sellerName,
          'الفئة': c.category,
          'قيمة الطلب': c.orderValue,
          'نسبة العمولة': (c.commissionRate * 100).toFixed(2) + '%',
          'قيمة العمولة': c.commissionAmount.toFixed(2),
          'التاريخ': c.createdAt.toLocaleDateString('ar-EG')
        }))
      })
    }
    
    if (coupons.length > 0) {
      sheets.push({
        name: 'الكوبونات',
        data: coupons.map(c => ({
          'رمز الكوبون': c.couponCode,
          'قيمة الخصم': c.discountAmount.toFixed(2),
          'معرف المستخدم': c.userId,
          'التاريخ': c.createdAt.toLocaleDateString('ar-EG')
        }))
      })
    }
    
    if (walletTransactions.length > 0) {
      sheets.push({
        name: 'معاملات المحفظة',
        data: walletTransactions.map(t => ({
          'النوع': t.type,
          'المبلغ': t.amount.toFixed(2),
          'اسم المستخدم': t.userName,
          'الحالة': t.status,
          'التاريخ': t.createdAt.toLocaleDateString('ar-EG')
        }))
      })
    }
    
    if (sheets.length > 0) {
      exportMultipleSheetsToExcel(
        sheets,
        `financial_data_${new Date().toISOString().split('T')[0]}`
      )
    }
  }

  const handleExportCSV = (type: 'commissions' | 'coupons' | 'wallets') => {
    switch (type) {
      case 'commissions':
        if (commissions.length > 0) {
          exportToCSV(
            commissions.map(c => ({
              sellerName: c.sellerName,
              category: c.category,
              orderValue: c.orderValue.toFixed(2),
              commissionRate: (c.commissionRate * 100).toFixed(2) + '%',
              commissionAmount: c.commissionAmount.toFixed(2),
              date: c.createdAt.toLocaleDateString('ar-EG')
            })),
            `commissions_${new Date().toISOString().split('T')[0]}`
          )
        }
        break
      case 'coupons':
        if (coupons.length > 0) {
          exportToCSV(
            coupons.map(c => ({
              couponCode: c.couponCode,
              discountAmount: c.discountAmount.toFixed(2),
              userId: c.userId,
              date: c.createdAt.toLocaleDateString('ar-EG')
            })),
            `coupons_${new Date().toISOString().split('T')[0]}`
          )
        }
        break
      case 'wallets':
        if (walletTransactions.length > 0) {
          exportToCSV(
            walletTransactions.map(t => ({
              type: t.type,
              amount: t.amount.toFixed(2),
              userName: t.userName,
              status: t.status,
              date: t.createdAt.toLocaleDateString('ar-EG')
            })),
            `wallet_transactions_${new Date().toISOString().split('T')[0]}`
          )
        }
        break
    }
  }

  // Prepare chart data
  const commissionsByCategoryData = financialData
    ? Object.entries(financialData.commissionsByCategory).map(([category, amount]) => ({
        name: category,
        value: amount
      }))
    : []

  const paymentMethodsData = financialData
    ? Object.entries(financialData.paymentMethodsBreakdown).map(([method, amount]) => ({
        name: method,
        value: amount
      }))
    : []

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight="bold">
            التقارير المالية
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={loadData}
              disabled={loading}
            >
              تحديث
            </Button>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdf />}
              onClick={handleExportPDF}
              disabled={!financialData}
            >
              تصدير PDF
            </Button>
            <Button
              variant="outlined"
              startIcon={<TableChart />}
              onClick={handleExportExcel}
              disabled={loading}
            >
              تصدير Excel
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>الفترة</InputLabel>
                <Select
                  value={period}
                  label="الفترة"
                  onChange={(e) => handlePeriodChange(e.target.value)}
                >
                  <MenuItem value="">لا شيء</MenuItem>
                  <MenuItem value="today">اليوم</MenuItem>
                  <MenuItem value="7days">آخر 7 أيام</MenuItem>
                  <MenuItem value="30days">آخر 30 يوم</MenuItem>
                  <MenuItem value="thisMonth">هذا الشهر</MenuItem>
                  <MenuItem value="custom">مخصص</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {period === 'custom' && (
              <>
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="من تاريخ"
                    value={startDate}
                    onChange={(date) => date && setStartDate(date)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="إلى تاريخ"
                    value={endDate}
                    onChange={(date) => date && setEndDate(date)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Paper>

        {period === '' ? (
          <Box sx={{ py: 4 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              يرجى اختيار فترة من القائمة المنسدلة أعلاه لعرض البيانات
            </Alert>
          </Box>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={60} />
          </Box>
        ) : (
          <>
            {/* Financial Dashboard KPIs */}
            <Box sx={{ mb: 3 }}>
              <FinancialDashboard data={financialData} loading={loading} />
            </Box>

            {/* Charts */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {/* Revenue & Commissions Time Series */}
              {timeSeriesData.length > 0 && (
                <Grid item xs={12} md={12}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      الإيرادات والعمولات عبر الزمن
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={timeSeriesData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="revenue" stroke="#4CAF50" name="الإيرادات" />
                        <Line type="monotone" dataKey="commissions" stroke="#2196F3" name="العمولات" />
                        <Line type="monotone" dataKey="discounts" stroke="#FF9800" name="الخصومات" />
                      </LineChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
              )}

              {/* Commissions by Category */}
              {commissionsByCategoryData.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      العمولات حسب الفئة
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={commissionsByCategoryData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(props: any) => `${props.name} ${((props.percent || 0) * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {commissionsByCategoryData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
              )}

              {/* Payment Methods */}
              {paymentMethodsData.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      طرق الدفع
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={paymentMethodsData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="value" fill="#FF6600" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
              )}
            </Grid>

            {/* Top Sellers Table */}
            {financialData && financialData.topSellers.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <FinancialTable
                  title="أعلى 10 تجار دخلاً"
                  headers={['اسم التاجر', 'الإيرادات', 'العمولة']}
                  rows={financialData.topSellers.map(seller => [
                    seller.name,
                    `$${seller.revenue.toFixed(2)}`,
                    `$${seller.commission.toFixed(2)}`
                  ])}
                  loading={loading}
                />
              </Box>
            )}

            {/* Detailed Tables Tabs */}
            <Paper sx={{ width: '100%' }}>
              <Tabs
                value={currentTab}
                onChange={(e, newValue) => setCurrentTab(newValue)}
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label="العمولات" />
                <Tab label="الكوبونات المستخدمة" />
                <Tab label="معاملات المحفظة" />
              </Tabs>

              <TabPanel value={currentTab} index={0}>
                <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FileDownload />}
                    onClick={() => handleExportCSV('commissions')}
                  >
                    تصدير CSV
                  </Button>
                </Stack>
                <FinancialTable
                  title=""
                  headers={['اسم التاجر', 'الفئة', 'قيمة الطلب', 'نسبة العمولة', 'قيمة العمولة', 'التاريخ']}
                  rows={commissions.map(c => [
                    c.sellerName,
                    c.category,
                    `$${c.orderValue.toFixed(2)}`,
                    `${(c.commissionRate * 100).toFixed(2)}%`,
                    `$${c.commissionAmount.toFixed(2)}`,
                    c.createdAt.toLocaleDateString('ar-EG')
                  ])}
                  loading={loading}
                />
              </TabPanel>

              <TabPanel value={currentTab} index={1}>
                <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FileDownload />}
                    onClick={() => handleExportCSV('coupons')}
                  >
                    تصدير CSV
                  </Button>
                </Stack>
                <FinancialTable
                  title=""
                  headers={['رمز الكوبون', 'قيمة الخصم', 'معرف المستخدم', 'التاريخ']}
                  rows={coupons.map(c => [
                    c.couponCode,
                    `$${c.discountAmount.toFixed(2)}`,
                    c.userId,
                    c.createdAt.toLocaleDateString('ar-EG')
                  ])}
                  loading={loading}
                />
              </TabPanel>

              <TabPanel value={currentTab} index={2}>
                <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FileDownload />}
                    onClick={() => handleExportCSV('wallets')}
                  >
                    تصدير CSV
                  </Button>
                </Stack>
                <FinancialTable
                  title=""
                  headers={['النوع', 'المبلغ', 'اسم المستخدم', 'الحالة', 'التاريخ']}
                  rows={walletTransactions.map(t => [
                    t.type,
                    `$${t.amount.toFixed(2)}`,
                    t.userName,
                    t.status,
                    t.createdAt.toLocaleDateString('ar-EG')
                  ])}
                  loading={loading}
                />
              </TabPanel>
            </Paper>
          </>
        )}
      </Box>
    </LocalizationProvider>
  )
}
